import {Separator, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isEmpty} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {DmsDataGrid, catchUnhandledDataGridActions, logError} from '@dms/shared';

import {TestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {CORE_EXCHANGE_ITEM_SELECT_GRID_CODE} from './CORE_EXCHANGE_ITEM_SELECT_GRID_CODE';

const CORE_EXCHANGE_EXTERNAL_FILTER_ID = 'ds-core-exchange-external-filter';

interface CoreExchangeTabProps extends TestIdProps {
  onCoreExchangeDispatch: (coreExchangeItemId: string) => Promise<boolean>;
  hasPendingRequest: boolean;
}

export function RequestTabMaterialCoreExchangeTab(props: CoreExchangeTabProps) {
  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  // ACL (core exchange + direct sale edit) is enforced by the BE on the whole grid
  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const coreExchangeItemIds = isArray(rowId) ? rowId : [rowId];

    if (isEmpty(coreExchangeItemIds)) {
      return logError('No row selected');
    }

    // The endpoint takes one item, so a bulk action dispatches the rows one by one
    // and stops at the first failure to avoid a toast per remaining row
    const dispatchAll = () =>
      coreExchangeItemIds.reduce<Promise<boolean>>(
        (previous, coreExchangeItemId) =>
          previous.then((isDispatched) =>
            isDispatched ? props.onCoreExchangeDispatch(coreExchangeItemId) : false
          ),
        Promise.resolve(true)
      );

    return match(actionKey)
      .with('addToBasket', () => dispatchAll().then(() => refreshData()))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <HStack height="100%" data-testid={props['data-testid']}>
      <Box flex={1}>
        <div id={CORE_EXCHANGE_EXTERNAL_FILTER_ID}></div>
      </Box>

      <Separator orientation="vertical" />

      <Box flex={3}>
        <DmsDataGrid
          gridCode={CORE_EXCHANGE_ITEM_SELECT_GRID_CODE}
          autoFocusSmartSearch
          actionCallback={actionCallback}
          externalFilterId={CORE_EXCHANGE_EXTERNAL_FILTER_ID}
          data-testid={CORE_EXCHANGE_ITEM_SELECT_GRID_CODE}
        />
      </Box>
    </HStack>
  );
}
